import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  CheckCircle,
  Shield,
  Clock,
  Zap,
  Users,
} from "lucide-react";
import { SectionHeader } from "../components/SectionHeader";
import { CTABand } from "../components/CTABand";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoModal } from "../components/BookDemoModal";

// How it works steps
const steps = [
  {
    step: 1,
    icon: <Upload className="h-8 w-8" />,
    title: "Upload Your Documents",
    description: "Simply upload your compliance documents to our secure platform. We support all major formats including PDF, Word, and Excel."
  },
  {
    step: 2,
    icon: <FileText className="h-8 w-8" />,
    title: "AI Analysis",
    description: "Our advanced AI analyzes your documents against current compliance standards, identifying gaps and outdated requirements."
  },
  {
    step: 3,
    icon: <CheckCircle className="h-8 w-8" />,
    title: "Get Recommendations",
    description: "Receive detailed recommendations with specific changes needed to bring your documents up to current compliance standards."
  },
  {
    step: 4,
    icon: <Shield className="h-8 w-8" />,
    title: "Stay Compliant",
    description: "Your documents are automatically updated when regulations change, ensuring continuous compliance without manual effort."
  }
];

// Benefits
const benefits = [
  {
    icon: <Clock className="h-6 w-6" />,
    title: "Save 80% Time",
    description: "What used to take hours now takes minutes"
  },
  {
    icon: <Shield className="h-6 w-6" />,
    title: "Eliminate Risk",
    description: "Never miss compliance updates again"
  },
  {
    icon: <Zap className="h-6 w-6" />,
    title: "Instant Updates",
    description: "Automatic updates when regulations change"
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: "Team Collaboration",
    description: "Work together seamlessly across departments"
  }
];

export default function HowItWorksPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0D2B22", color: "#FFFFFF" }}>
      <Navbar />
      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <div className="py-16 sm:py-20 lg:py-24">
            <div className="text-center max-w-4xl mx-auto">
              <motion.h1
                className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6 font-merriweather"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                How TemplaSure Works
              </motion.h1>
              <motion.p
                className="text-base sm:text-lg md:text-xl leading-relaxed mb-6 sm:mb-8 px-4"
                style={{ color: "#9ABFB5" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              >
                Transform your compliance process in four simple steps.
                Our AI-powered platform makes compliance management effortless and automated.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.4 }}
              >
                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="rounded-xl bg-[#2EB39A] px-8 py-3 font-bold text-[#0D2B22] hover:bg-[#25927E] transition-colors"
                >
                  Book a Demo
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Steps */}
      <section className="section-padding" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <SectionHeader
            badge="Simple Process"
            title="Four steps to compliance automation"
            subtitle="See how easy it is to transform your compliance management with TemplaSure."
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
              >
                <div className="relative mb-6">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg text-[#2EB39A]"
                    style={{ backgroundColor: "#1A4035" }}
                  >
                    {step.icon}
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ backgroundColor: "#2EB39A", color: "#0D2B22" }}
                  >
                    {step.step}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 font-merriweather">
                  {step.title}
                </h3>
                <p className="leading-relaxed font-lato" style={{ color: "#9ABFB5" }}>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding" style={{ backgroundColor: "#0D2B22" }}>
        <div className="mx-auto max-w-7xl container-padding">
          <SectionHeader
            badge="Why Choose TemplaSure"
            title="Powerful benefits for your team"
            subtitle="Experience the transformation that compliance automation brings to your organisation."
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="rounded-2xl p-6 shadow-lg border border-[#1A4035] text-center hover:shadow-xl transition-shadow duration-300"
                style={{ backgroundColor: "#0F3328" }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
              >
                <div
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg text-[#2EB39A]"
                  style={{ backgroundColor: "#1A4035" }}
                >
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-3 font-merriweather">
                  {benefit.title}
                </h3>
                <p className="leading-relaxed font-lato" style={{ color: "#9ABFB5" }}>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="early-access">
        <CTABand
          title="Ready to automate your compliance process?"
          description="Join thousands of professionals already on our waiting list"
          onOpenDemo={() => setDemoOpen(true)}
        />
      </section>

      <Footer />
      <BookDemoModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
